import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaInstagram, FaPlay, FaPause } from 'react-icons/fa';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const scanline = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  50% { opacity: 0.3; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const shimmer = keyframes`
  0% { 
    background-position: -200% center;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% { 
    background-position: 200% center;
    opacity: 0.3;
  }
`;

const hologramFlicker = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
  75% { opacity: 0.95; }
`;

const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px) translateZ(0);
  }
  50% { 
    transform: translateY(-15px) translateZ(10px);
  }
`;

const techGridPulse = keyframes`
  0%, 100% { 
    opacity: 0.08;
    transform: scale(1);
  }
  50% { 
    opacity: 0.2;
    transform: scale(1.01);
  }
`;

const energyPulse = keyframes`
  0% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 40px rgba(100, 182, 255, 0.4),
      inset 0 0 30px rgba(100, 182, 255, 0.2);
  }
  100% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 30px 40px 30px;
  overflow: hidden;
  position: relative;
  perspective: 1000px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(100, 182, 255, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 50%, rgba(0, 255, 255, 0.02) 0%, transparent 50%),
      linear-gradient(90deg, rgba(100, 182, 255, 0.02) 1px, transparent 1px),
      linear-gradient(rgba(100, 182, 255, 0.02) 1px, transparent 1px);
    background-size: 100% 100%, 100% 100%, 60px 60px, 60px 60px;
    animation: ${techGridPulse} 8s ease-in-out infinite;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, transparent 0%, rgba(0, 10, 20, 0.3) 100%);
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  background: linear-gradient(
    135deg,
    rgba(100, 182, 255, 0.9),
    rgba(0, 255, 255, 0.9),
    rgba(100, 182, 255, 0.9)
  );
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
  text-align: center;
  letter-spacing: 8px;
  font-size: 2rem;
  text-transform: uppercase;
  position: relative;
  font-weight: 300;
  filter: drop-shadow(0 0 30px rgba(100, 182, 255, 0.5));
  animation: ${shimmer} 6s ease-in-out infinite, ${hologramFlicker} 4s ease-in-out infinite;
  z-index: 20;
  
  &::before {
    content: '// ANÚNCIOS COM IA';
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(100, 182, 255, 0.4);
    font-weight: 300;
    font-size: 0.7rem;
    letter-spacing: 3px;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 0.8),
      rgba(100, 182, 255, 0.8),
      transparent
    );
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  }
`;

const ContentPreview = styled.div`
  max-width: 900px;
  width: 90%;
  margin: 0 auto;
  padding: 25px 35px 35px;
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 30px;
  background: 
    linear-gradient(
      135deg,
      rgba(10, 25, 45, 0.4) 0%,
      rgba(15, 35, 60, 0.3) 50%,
      rgba(10, 25, 45, 0.4) 100%
    ),
    rgba(0, 0, 0, 0.4);
  box-shadow: 
    0 20px 80px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(100, 182, 255, 0.15),
    inset 0 0 80px rgba(100, 182, 255, 0.03);
  backdrop-filter: blur(40px) saturate(150%);
  position: relative;
  overflow: visible;
  animation: ${energyPulse} 6s ease-in-out infinite;
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 5px 30px rgba(100, 182, 255, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 -5px 30px rgba(100, 182, 255, 0.3);
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(100, 182, 255, 0.05) 30%,
    rgba(0, 255, 255, 0.08) 50%,
    rgba(100, 182, 255, 0.05) 70%,
    transparent 100%
  );
  animation: ${scanline} 8s ease-in-out infinite;
  pointer-events: none;
  z-index: 5;
  filter: blur(1px);
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 350px;
  margin: 0 auto 25px;
  position: relative;
  border-radius: 20px;
  overflow: visible;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(100, 182, 255, 0.3);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 40px rgba(100, 182, 255, 0.2),
    inset 0 0 40px rgba(100, 182, 255, 0.05);
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(
      45deg,
      rgba(100, 182, 255, 0.3),
      rgba(0, 255, 255, 0.3),
      rgba(100, 182, 255, 0.3)
    );
    background-size: 400% 400%;
    animation: ${shimmer} 8s ease-in-out infinite;
    border-radius: 20px;
    z-index: -1;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 177.78%;
  background: #000;
  border-radius: 20px;
  overflow: hidden;
  clip-path: inset(0 round 20px);
`;

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px 20px;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.6) 60%,
    transparent 100%
  );
  display: flex;
  align-items: center;
  gap: 15px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 20;
  
  ${VideoContainer}:hover & {
    opacity: 1;
  }
`;

const PlayButton = styled.button`
  background: rgba(100, 182, 255, 0.2);
  border: 1px solid rgba(100, 182, 255, 0.5);
  color: #64B6FF;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(100, 182, 255, 0.3);
    border-color: rgba(100, 182, 255, 0.8);
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.4);
    transform: scale(1.1);
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background: linear-gradient(
      90deg,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 0.8)
    );
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    transition: width 0.1s linear;
  }
`;

const InstagramLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 28px;
  background: 
    linear-gradient(
      135deg,
      rgba(100, 182, 255, 0.1) 0%,
      rgba(0, 255, 255, 0.08) 50%,
      rgba(100, 182, 255, 0.1) 100%
    ),
    rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 50px;
  color: #64B6FF;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.95rem;
  font-weight: 300;
  letter-spacing: 1.5px;
  text-decoration: none;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 30px rgba(100, 182, 255, 0.2);
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(100, 182, 255, 0.2) 0%,
      rgba(0, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    transition: width 0.6s ease, height 0.6s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50px;
    padding: 2px;
    background: linear-gradient(
      135deg,
      rgba(100, 182, 255, 0.5),
      rgba(0, 255, 255, 0.5)
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    border-color: rgba(100, 182, 255, 0.6);
    color: #00FFFF;
    box-shadow: 
      0 10px 40px rgba(100, 182, 255, 0.4),
      0 0 60px rgba(100, 182, 255, 0.3),
      inset 0 0 30px rgba(100, 182, 255, 0.1);
    
    &::before {
      width: 400px;
      height: 400px;
    }
    
    &::after {
      opacity: 1;
    }
    
    svg {
      transform: rotate(15deg) scale(1.1);
    }
  }
  
  svg {
    font-size: 1.2rem;
    filter: drop-shadow(0 0 10px rgba(100, 182, 255, 0.6));
    transition: transform 0.4s ease;
  }
`;

const TechParticle = styled.div`
  position: absolute;
  width: 3px;
  height: 3px;
  background: radial-gradient(circle, #64B6FF, #00FFFF);
  border-radius: 50%;
  box-shadow: 
    0 0 15px rgba(100, 182, 255, 1),
    0 0 30px rgba(100, 182, 255, 0.5);
  animation: 
    ${floatAnimation} 4s ease-in-out infinite,
    ${glowPulse} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  top: ${props => props.$top}%;
  right: ${props => props.$right}%;
  opacity: 0.7;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(100, 182, 255, 0.3), transparent 70%);
    border-radius: 50%;
    animation: ${glowPulse} 2s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
  }
`;

const HolographicCorner = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  ${props => {
    switch(props.$position) {
      case 'top-left':
        return 'top: 0; left: 0; border-top: 2px solid; border-left: 2px solid;';
      case 'top-right':
        return 'top: 0; right: 0; border-top: 2px solid; border-right: 2px solid;';
      case 'bottom-left':
        return 'bottom: 0; left: 0; border-bottom: 2px solid; border-left: 2px solid;';
      case 'bottom-right':
        return 'bottom: 0; right: 0; border-bottom: 2px solid; border-right: 2px solid;';
      default:
        return '';
    }
  }}
  border-color: rgba(100, 182, 255, 0.4);
  animation: ${glowPulse} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  pointer-events: none;
  z-index: 15;
  
  &::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background: radial-gradient(circle, rgba(100, 182, 255, 0.8), transparent);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.8);
    ${props => {
      switch(props.$position) {
        case 'top-left':
          return 'top: -3px; left: -3px;';
        case 'top-right':
          return 'top: -3px; right: -3px;';
        case 'bottom-left':
          return 'bottom: -3px; left: -3px;';
        case 'bottom-right':
          return 'bottom: -3px; right: -3px;';
        default:
          return '';
      }
    }}
  }
`;

const PresentationSlide14 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide14', lang);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  return (
    <SlideWrapper>
      <SlideTitle>{t.title}</SlideTitle>
      
      <ContentPreview>
        <ScanLine />
        
        <HolographicCorner $position="top-left" $delay={0} />
        <HolographicCorner $position="top-right" $delay={0.5} />
        <HolographicCorner $position="bottom-left" $delay={1} />
        <HolographicCorner $position="bottom-right" $delay={1.5} />
        
        <TechParticle $top={10} $right={5} $delay={0} />
        <TechParticle $top={20} $right={95} $delay={0.5} />
        <TechParticle $top={35} $right={8} $delay={0.8} />
        <TechParticle $top={45} $right={92} $delay={1.2} />
        <TechParticle $top={60} $right={5} $delay={1.6} />
        <TechParticle $top={75} $right={95} $delay={2} />

        <VideoContainer>
          <VideoWrapper>
            <StyledVideo
              ref={videoRef}
              onTimeUpdate={handleTimeUpdate}
              onClick={togglePlay}
            >
              <source src="/imoveis/5/Austin - Texas - Final.mp4" type="video/mp4" />
            </StyledVideo>
            
            <VideoControls>
              <PlayButton onClick={togglePlay}>
                {isPlaying ? <FaPause /> : <FaPlay />}
              </PlayButton>
              <ProgressBar 
                $progress={progress} 
                onClick={handleProgressClick}
              />
            </VideoControls>
          </VideoWrapper>
        </VideoContainer>

        {lang === 'pt' && (
          <InstagramLink 
            href="https://www.instagram.com/p/DPppKynDlFk/#advertiser"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaInstagram />
            {t.viewOnInstagram}
          </InstagramLink>
        )}
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide14;
